import type {
  ColumnUnitToggle,
  RepeatableColumn,
  RepeatableSumValidation,
  WorkflowField,
} from '@/api/workflows';

import type { FieldValue, RepeatableRowValue } from './FieldRenderer';

/** The two units a dual-unit column switches between. */
export type UnitMode = 'dollar' | 'percent';

/**
 * Value key holding one dual-unit column's current mode.
 *
 * Namespaced so it can never collide with a field id (those are GUIDs), and
 * keyed by COLUMN because the toggle governs a whole column across every row —
 * live RIF draws it once in the column header, not per row.
 */
export function unitModeKey(fieldId: string, columnId: string): string {
  return `unitMode:${fieldId}:${columnId}`;
}

/** The column's current mode, falling back to its authored default. */
export function readUnitMode(
  field: WorkflowField,
  column: RepeatableColumn,
  getValueById?: (id: string) => FieldValue,
): UnitMode {
  const authored = column.unitToggle?.defaultMode === 'percent' ? 'percent' : 'dollar';
  if (!getValueById) return authored;
  const raw = getValueById(unitModeKey(field.id, column.id));
  return raw === 'percent' || raw === 'dollar' ? raw : authored;
}

/**
 * The sum target that applies to a dual-unit column in its CURRENT mode, or
 * null when that mode carries no total rule.
 *
 * RIF constrains percentages only — dollar mode has no rule — so "no target"
 * is a legitimate configuration, not a misconfiguration.
 */
export function resolveUnitModeTarget(
  toggle: ColumnUnitToggle,
  mode: UnitMode,
  getValueById?: (id: string) => FieldValue,
): number | null {
  const constant = mode === 'percent' ? toggle.percentTargetConstant : toggle.dollarTargetConstant;
  const fieldId = mode === 'percent' ? toggle.percentTargetFieldId : toggle.dollarTargetFieldId;
  if (typeof constant === 'number' && Number.isFinite(constant)) return constant;
  if (fieldId && getValueById) {
    const raw = getValueById(fieldId);
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

/**
 * Parse a currency-formatted number string. The FE Number input's live
 * formatter inserts thousand separators (`1,000.00`); Number.parseFloat
 * silently truncates at the first non-digit — `parseFloat('1,000.00')`
 * is 1, not 1000 — so a straight parseFloat on a cell value produces
 * false sum-mismatch and blocks Next. Strip common thousand separators
 * (comma, space, narrow-no-break-space used by fr-CA locale) before
 * handing to parseFloat.
 */
function parseCurrencyString(s: string): number {
  const cleaned = s.replace(/[,\s]/g, '');
  return Number.parseFloat(cleaned);
}

/**
 * Compute the sum of a specified column across every row of a RepeatableRow
 * field. Non-numeric cells contribute 0; empty rows contribute nothing.
 * Returns null when the field carries no sumValidation (caller signal for
 * "nothing to check").
 */
export function computeRepeatableSum(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  /** Defaults to the field's own sumValidation column; pass a column id to
   *  total a dual-unit column instead. */
  columnKey?: string,
): number | null {
  const key = columnKey ?? field.sumValidation?.columnKey;
  if (!key) return null;
  let total = 0;
  for (const row of rows) {
    const cell = row[key];
    if (typeof cell === 'number' && Number.isFinite(cell)) {
      total += cell;
    } else if (typeof cell === 'string' && cell.trim().length > 0) {
      const parsed = parseCurrencyString(cell);
      if (Number.isFinite(parsed)) total += parsed;
    }
  }
  return total;
}

/**
 * Resolve the target sum for a RepeatableRow's sumValidation. Prefers the
 * absolute `targetConstant`; falls back to the current value of the referenced
 * `targetFieldId`. Returns null when neither is set OR the referenced field
 * hasn't been filled — the caller treats "no target yet" as "no check yet"
 * so the runtime doesn't complain before the advisor has typed the anchor.
 */
export function resolveSumTarget(
  sv: RepeatableSumValidation,
  getValueById?: (id: string) => FieldValue,
): number | null {
  if (typeof sv.targetConstant === 'number' && Number.isFinite(sv.targetConstant)) {
    return sv.targetConstant;
  }
  if (sv.targetFieldId && getValueById) {
    const raw = getValueById(sv.targetFieldId);
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

/**
 * Convenience — returns true when the RepeatableRow's actual sum matches
 * its target (within a small epsilon to survive floating-point noise). Also
 * returns true when the rule doesn't apply (no sumValidation) OR the target
 * isn't yet resolvable — the "not yet checkable" cases must not block Next.
 */
export function isSumSatisfied(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  getValueById?: (id: string) => FieldValue,
): boolean {
  const sv = field.sumValidation;
  if (sv) {
    const target = resolveSumTarget(sv, getValueById);
    const total = computeRepeatableSum(field, rows);
    if (target !== null && total !== null && Math.abs(total - target) >= 0.005) return false;
  }
  return unbalancedUnitColumns(field, rows, getValueById).length === 0;
}

/**
 * Dual-unit columns whose total does not match the target for their CURRENT
 * mode. Empty when every column balances, when no column is dual-unit, or when
 * the active mode carries no rule — RIF's dollar mode is unconstrained, and
 * "not checkable yet" must never block Next.
 *
 * Returned rather than reduced to a boolean so the caller can name the offending
 * column in the Next tooltip.
 */
export function unbalancedUnitColumns(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  getValueById?: (id: string) => FieldValue,
): { column: RepeatableColumn; mode: UnitMode; total: number; target: number }[] {
  const out: { column: RepeatableColumn; mode: UnitMode; total: number; target: number }[] = [];
  for (const column of field.repeatableTemplate ?? []) {
    const toggle = column.unitToggle;
    if (!toggle) continue;
    const mode = readUnitMode(field, column, getValueById);
    const target = resolveUnitModeTarget(toggle, mode, getValueById);
    if (target === null) continue;
    const total = computeRepeatableSum(field, rows, column.id);
    if (total === null) continue;
    if (Math.abs(total - target) >= 0.005) out.push({ column, mode, total, target });
  }
  return out;
}
